import { createFeatureSelector, createSelector, MemoizedSelector } from '@ngrx/store';

import { InstalledAppleApplication } from '@era/locations-sdk';
import { Device } from '@rocksoft/shared';
import { groupBy, map, uniqBy } from 'lodash-es';
import { DeviceStoreSelectors } from '../device-store';
import { Category } from '../router/category';
import * as fromCategoryApplications from './category-applications.reducer';
import { ApplicationConfiguration, CategoryApplicationsState } from './category-applications.state';

// ============================================================================
// STATE SELECTORS
// ============================================================================

export const selectCategoryApplicationsState = createFeatureSelector<CategoryApplicationsState>('categoryApplications');

export const selectAllCategoryApplications = createSelector(
  selectCategoryApplicationsState,
  fromCategoryApplications.selectAllCategoryApplications
);

// ============================================================================
// FACTORY FUNCTIONS
// ============================================================================

// Generic selector factory functions that work with any CategoryType
// These eliminate the need to replicate selectors for every category type

export const createSelectApplicationsLinkedToCategory = (category: Category) => {
  return createSelector(selectAllCategoryApplications, (applications: ApplicationConfiguration[]) => {
    return applications.filter(
      (application: ApplicationConfiguration) =>
        application.categoryId === category.categoryId && application.categoryType === category.categoryType
    );
  });
};

export const createSelectApplicationsLinkedToCategoryLoaded = (category: Category) => {
  return createSelector(
    createSelectApplicationsLinkedToCategory(category),
    (applications: ApplicationConfiguration[]) => applications && applications.length > 0
  );
};

export const createSelectDistinctApplicationsLinkedToCategory = (category: Category) => {
  return createSelector(
    createSelectApplicationsLinkedToCategory(category),
    (applications: ApplicationConfiguration[]) =>
      uniqBy(applications, (app: ApplicationConfiguration) => app.bundleIdentifier)
  );
};

export const createSelectDistinctBundleIdentifiersLinkedToCategory = (category: Category) => {
  return createSelector(
    createSelectDistinctApplicationsLinkedToCategory(category),
    (applications: ApplicationConfiguration[]) =>
      applications.map((application: ApplicationConfiguration) => application.bundleIdentifier)
  );
};

// ============================================================================
// GENERIC SELECTORS - One-liner selectors for containers
// ============================================================================

/**
 * Generic selector for applications applied to any category type (with inheritance).
 * Automatically handles router selector mapping and inheritance logic.
 *
 * Usage in containers:
 * this.inheritedApplications$ = this.store.select(selectApplicationsAppliedToCategoryType('Location'));
 */
export const selectApplicationsAppliedToCategory = (
  category: Category
): MemoizedSelector<object, ApplicationConfiguration[]> => {
  // HACK: These categories do not support inheritance for applications, so we return the applications linked to the category directly.
  // This is a temporary solution until we have a proper plan for our inheritance logic, see user story #35828.
  if (
    category.categoryType === 'Bar' ||
    category.categoryType === 'FrontDesk' ||
    category.categoryType === 'Housekeeping' ||
    category.categoryType === 'Kiosk' ||
    category.categoryType === 'Staff'
  ) {
    return createSelectApplicationsLinkedToCategory(category);
  }

  if (category.parent) {
    return createSelector(
      selectApplicationsAppliedToCategory(category.parent),
      createSelectApplicationsLinkedToCategory(category),
      (parent, current) => [...parent, ...current]
    );
  } else {
    return createSelectApplicationsLinkedToCategory(category);
  }
};

// ============================================================================
// APPLE DEVICE SELECTORS
// ============================================================================

export interface InstalledAppleApplicationWithConfiguration extends InstalledAppleApplication {
  isConfiguredVersion: boolean;
}

/**
 * Selector for applications with configuration flags for the current Apple device.
 * Used by Apple device containers to show configuration status.
 */
export const selectApplicationsForDeviceWithConfiguration = (category: Category, device: Device) =>
  createSelector(
    DeviceStoreSelectors.selectDeviceByUdid(device.udid),
    selectApplicationsAppliedToCategory(category),
    (device: Device, applications: ApplicationConfiguration[]) => addIsConfiguredFlagToAppleDevice(device, applications)
  );

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Adds configuration flags to Apple device applications.
 * This function processes applications and adds version configuration status.
 */
const addIsConfiguredFlagToAppleDevice = (device: Device, applications: ApplicationConfiguration[]) => {
  if (device === undefined) {
    return undefined;
  }

  // Guard: if managedApplications is not loaded yet, return the device as-is
  if (!device.managedApplications) {
    return device;
  }

  const groups = groupBy(applications, (x: ApplicationConfiguration) => x.bundleIdentifier);
  applications = map(groups, (g: ApplicationConfiguration[]) => {
    const roomApp = g.find((config: ApplicationConfiguration) => config.categoryId !== undefined);
    const locationApp = g.find((config: ApplicationConfiguration) => config.categoryId !== undefined);
    const brandApp = g.find((config: ApplicationConfiguration) => config.categoryId !== undefined);

    if (roomApp !== undefined) {
      return roomApp;
    }

    return locationApp !== undefined ? locationApp : brandApp;
  }).filter((app): app is ApplicationConfiguration => app !== undefined);

  const applicationsWithFlag = device.managedApplications.map((managedApplication: InstalledAppleApplication) => {
    return {
      ...managedApplication,
      isConfiguredVersion: isConfiguredVersion(managedApplication, applications),
    };
  });

  return {
    ...device,
    managedApplications: applicationsWithFlag,
  };
};

/**
 * Groups applications by BundleIdentifier to show all versions (active and scheduled) in the same element.
 * This grouping is purely aesthetic and operations are still applied through the Store in the associated application-link component.
 */
export const getGroupedApplications = (
  applications: ApplicationConfiguration[]
): (ApplicationConfiguration & {
  state: 'active' | 'scheduled' | 'previous';
})[][] => {
  const groupedApplications = groupBy(applications, (app: ApplicationConfiguration) => app.bundleIdentifier);
  const groupedApplicationsArray = Object.values(groupedApplications);
  const filteredGroupedApplications = filterOutInheritedApps(groupedApplicationsArray);

  return filteredGroupedApplications.map((group: ApplicationConfiguration[]) => {
    return group.map((app: ApplicationConfiguration) => {
      const state = getState(app, group);

      const application: ApplicationConfiguration & {
        state: 'active' | 'scheduled' | 'previous';
      } = { ...app, state: state };

      return application;
    });
  });
};

/**
 * Determines the state of an application within a group (active, scheduled, or previous).
 */
const getState = (
  application: ApplicationConfiguration,
  group: ApplicationConfiguration[]
): 'active' | 'scheduled' | 'previous' => {
  const activeApp = getActiveApp(group);

  if (application === activeApp) {
    return 'active';
  } else if (application.applyFrom && new Date(application.applyFrom) > new Date()) {
    return 'scheduled';
  } else {
    return 'previous';
  }
};

/**
 * When an app has configurations on multiple levels, filter out the configs inherited from higher levels.
 * For example, filter out Location config when a config is present on Room level.
 */
const filterOutInheritedApps = (groupedApplications: ApplicationConfiguration[][]) => {
  return groupedApplications.map((apps: ApplicationConfiguration[]) => {
    // Guard, if there is only one app then there is no need to check for inheritance.
    if (apps.length == 1) {
      return apps;
    }

    // We check whether an active configuration exists within the non-inherited configs. If the active configuration is inherited, it is shown in the UI
    const roomLevelApps = apps.filter(
      (app: ApplicationConfiguration) => app.categoryId != null && app.categoryType === 'Room'
    );
    const hasRoomLevelConfiguration = roomLevelApps.length > 0;

    const locationLevelApps = apps.filter(
      (app: ApplicationConfiguration) => app.categoryId != null && app.categoryType === 'Location'
    );
    const hasLocationLevelConfiguration = locationLevelApps.length > 0;

    if (hasRoomLevelConfiguration && getActiveApp(roomLevelApps)) {
      return roomLevelApps;
    } else if (hasLocationLevelConfiguration && getActiveApp(locationLevelApps)) {
      return locationLevelApps;
    } else {
      return apps;
    }
  });
};

/**
 * Gets the active application from a list of versions.
 * The active app is the most recently applied app that isn't scheduled.
 */
export const getActiveApp = (versions: ApplicationConfiguration[]) => {
  // The active app is the most recently applied app that isn't scheduled. Don't mutate the existing array to not
  // alter the order
  const sorted = [...versions].sort(
    (a: ApplicationConfiguration, b: ApplicationConfiguration) =>
      new Date(b.applyFrom ?? 0).getTime() - new Date(a.applyFrom ?? 0).getTime()
  );
  const version = sorted.find(
    (app: ApplicationConfiguration) => new Date(app.applyFrom ?? 0).getTime() <= new Date().getTime()
  );

  return version;
};

/**
 * Checks if an installed Apple application version matches the configured version.
 */
const isConfiguredVersion = (
  application: InstalledAppleApplication,
  applications: ApplicationConfiguration[]
): boolean => {
  const x = applications.find((app: ApplicationConfiguration) => app.bundleIdentifier === application.identifier);
  // Guard: Not a wanted (configured InHouse) application,
  // So we don't know whether or not to show the version
  // warning.
  if (x === undefined) {
    return true;
  }

  return x.version === application.version;
};
